import React, { useState } from 'react'
import styled from 'styled-components'
import PageTitle from '../../_Components/Common/PageTitle/PageTitle'
import { DMM_API_HOST, DMM_API_KEY } from '../../_Components/Common/common'
import {
  usePeriodData,
  PeriodControls,
  GrowthValue,
  isPlaceholder,
  num,
  percent,
  Wrapper,
  Message,
  TableBox,
  Table,
  Th,
  Td,
  TotalFoot,
  Name,
  Note,
  Muted,
  Footnote,
} from './dataShared'

/*
 * FANZA 채팅 메시지 포인트 소비를 대화 모델별로 본다.
 * 집계는 fanza-api 의 /v4.0.1/admin-data/message-point 가 전부 담당하고 여기서는 표시만 한다.
 */
const URL = `${DMM_API_HOST}/v4.0.1/admin-data/message-point`

const TypeBox = styled.div`
  width: 100%;
  margin-bottom: 12px;
  overflow-x: auto;
  background-color: #fff;
  border: 1px solid #d2d6de;
`

/* 아래 모델 표와 달리 줄무늬를 넣지 않는다. 선택된 줄이 묻히지 않아야 한다. */
const TypeTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
`

const TypeRow = styled.tr`
  cursor: pointer;
  background-color: ${props => (props.$selected ? '#eaf3fb' : 'transparent')};

  &:hover {
    background-color: ${props => (props.$selected ? '#eaf3fb' : '#f7f7f7')};
  }
`

const MessagePointData = () => {
  const state = usePeriodData({ url: URL, headers: { apikey: DMM_API_KEY } })
  const { body, loading, error, prevLabel } = state
  const list = body.list || []
  const total = body.total || null

  /*
   * 대화 종류별 뷰는 전체와 같은 모양으로 한 응답에 함께 온다. 그래서 종류를 바꿔도
   * 다시 받아오지 않고 아래 모델 표만 갈아끼운다.
   */
  const [chatType, setChatType] = useState('all')
  const views = [{ key: 'all', label: '전체', list, total }, ...(body.byType || [])]
  const view = views.find(item => item.key === chatType) || views[0]

  const renderRow = (row, isTotal) => (
    <tr key={row.model}>
      <Td align="left">
        <Name faded={!isTotal && isPlaceholder(row.model)}>{row.model}</Name>
        {row.region !== '-' && <Note>{row.region}</Note>}
      </Td>
      <Td>
        <strong>{num(row.spend)}</strong>
        {!isTotal && <Note>{row.spendShare}%</Note>}
      </Td>
      <Td>{num(row.users)}</Td>
      <Td>{num(row.count)}</Td>
      <Td>{num(row.avgPerUser)}</Td>
      <Td>{num(row.avgSpendPerUser)}</Td>
      <Td>
        {row.avgUnit}pt
        {/* 같은 모델이라도 장기기억을 켜면 단가가 올라간다 — 그 비중이 여기 드러난다 */}
        {!!(row.unitMix || []).length && (
          <Note>{row.unitMix.map(mix => `${mix.unit}pt ${num(mix.count)}`).join(' · ')}</Note>
        )}
      </Td>
      <Td>{num(row.maxPerUser)}</Td>
      <Td>{num(row.singleUsers)}</Td>
      <Td>{percent(row.topUserShare)}</Td>
      <Td>
        <GrowthValue value={row.growth && row.growth.spend} />
      </Td>
    </tr>
  )

  return (
    <Wrapper>
      <PageTitle
        mainTitle={'Message Point Data'}
        v1Link={''}
        navNames={['Data Analytics', 'Message Point Data']}
      />

      <PeriodControls
        state={state}
        summary={
          !!total && (
            <span>
              메시지 {num(total.count)}건 · 유저 {num(total.users)}명 · 소비{' '}
              {num(total.spend)}pt
            </span>
          )
        }
      />

      {error && <Message>{error}</Message>}
      {loading && !error && <Message>불러오는 중…</Message>}

      {/* 종류별 합계를 늘 펼쳐 두고, 줄을 누르면 아래 모델 표가 그 종류로 좁혀진다 */}
      {!loading && !error && views.length > 1 && (
        <TypeBox>
          <TypeTable>
            <thead>
              <tr>
                <Th align="left">대화 종류</Th>
                <Th>소비 포인트</Th>
                <Th>유저 수</Th>
                <Th>메시지 수</Th>
                <Th>인당 메시지</Th>
                <Th title="메시지 1건당 평균 차감 포인트">평균 단가</Th>
                <Th title={prevLabel && `직전 기간 ${prevLabel}`}>직전 기간 대비</Th>
              </tr>
            </thead>
            <tbody>
              {views.map(item => (
                <TypeRow
                  key={item.key}
                  $selected={item.key === chatType}
                  onClick={() => setChatType(item.key)}
                >
                  <Td align="left">
                    <Name faded={isPlaceholder(item.label)}>{item.label}</Name>
                    {item.key !== 'all' && !!(total && total.spend) && (
                      <Note>{percent(Math.round((item.total.spend / total.spend) * 1000) / 10)}</Note>
                    )}
                  </Td>
                  <Td>
                    <strong>{num(item.total && item.total.spend)}</strong>
                  </Td>
                  <Td>{num(item.total && item.total.users)}</Td>
                  <Td>{num(item.total && item.total.count)}</Td>
                  <Td>{num(item.total && item.total.avgPerUser)}</Td>
                  <Td>{item.total ? `${item.total.avgUnit}pt` : '-'}</Td>
                  <Td>
                    <GrowthValue value={item.total && item.total.growth && item.total.growth.spend} />
                  </Td>
                </TypeRow>
              ))}
            </tbody>
          </TypeTable>
        </TypeBox>
      )}

      {!loading && !error && (
        <TableBox>
          <Table>
            <thead>
              <tr>
                <Th align="left">대화 모델{chatType === 'all' ? '' : ` · ${view.label}`}</Th>
                <Th>소비 포인트</Th>
                <Th>유저 수</Th>
                <Th>메시지 수</Th>
                <Th>인당 메시지</Th>
                <Th>인당 포인트</Th>
                <Th title="메시지 1건당 평균 차감 포인트">평균 단가</Th>
                <Th>최다 유저</Th>
                <Th title="그 기간에 유료 메시지를 1건만 보낸 유저">1회 유저</Th>
                <Th title="포인트를 가장 많이 쓴 상위 10% 유저의 소비 비중">상위 10% 점유</Th>
                {/* 비교 대상은 바로 앞의 같은 길이 기간 */}
                <Th title={prevLabel && `직전 기간 ${prevLabel}`}>직전 기간 대비</Th>
              </tr>
            </thead>
            <tbody>
              {(view.list || []).map(row => renderRow(row, false))}
              {!(view.list || []).length && (
                <tr>
                  <Td colSpan={11}>
                    <Muted>
                      이 기간에 차감된{chatType === 'all' ? '' : ` ${view.label}`} 메시지 포인트가
                      없습니다.
                    </Muted>
                  </Td>
                </tr>
              )}
            </tbody>
            {!!(view.list || []).length && view.total && (
              <TotalFoot>{renderRow(view.total, true)}</TotalFoot>
            )}
          </Table>
        </TableBox>
      )}

      <Footnote>
        채팅 메시지 1건마다 남는 DMM 포인트 차감(STATUS = m)을 대화 상대 캐릭터의
        모델로 묶은 것입니다. 무료 프로모션 기간의 캐릭터는 차감이 일어나지 않아
        여기 잡히지 않습니다. 같은 모델이라도 장기기억을 켜면 단가가 올라가므로,
        「평균 단가」와 그 아래 단가 분포가 곧 장기기억 사용 비중입니다. 유저 수는
        모델별로 따로 세기 때문에 여러 모델을 쓴 유저는 각 줄에 모두 잡힙니다 —
        모델 행을 더해도 합계 유저 수와 같지 않습니다(포인트와 메시지 수는
        일치합니다). 「상위 10% 점유」는 포인트를 가장 많이 쓴 상위 10% 유저가
        차지하는 소비 비중으로, 매출이 소수에게 몰려 있는 정도를 봅니다.
        「직전 기간 대비」는 바로 앞의 같은 길이 기간
        {prevLabel ? `(${prevLabel})` : ''}과 비교한 소비 포인트 증감입니다.
        「(삭제된 대화)」「(삭제된 캐릭터)」는 차감 기록만 남고 대화방이나 캐릭터가
        지워져 모델을 되짚을 수 없는 건입니다.
        <br />
        위쪽 「대화 종류」는 포인트를 어디서 썼는지로 나눈 것입니다. 줄을 누르면 아래
        모델 표가 그 종류만 보여줍니다. 「1:1」은 캐릭터와 단둘이 나눈 대화, 「그룹챗」
        은 여러 캐릭터가 있는 방, 「NTR 대화」는 NTR 방에서 오간 대사입니다.
        「NTR 엿보기·미디어」는 대사가 아니라 엿보기·컷 해제·이미지·영상 차감이라
        따로 뺐습니다 — 단가가 20~150pt 로 대화와 크게 달라 섞으면 「메시지 수」와
        「평균 단가」가 둘 다 흐려집니다.
        <br />
        <strong>과거 구간에서는 그룹챗과 NTR 이 실제보다 적게 잡힙니다.</strong> 예전에는
        차감 기록에 대화방이 안 적혀 있어서 「유저 + 대화방 + 그 캐릭터」로 되짚었는데,
        방에는 대표 캐릭터 한 명만 등록돼 있어 나머지 캐릭터가 답한 차감은 방을 찾지
        못하고 「(알 수 없음)」으로 빠졌습니다. 지금은 차감할 때 방과 사유를 직접
        기록하므로 그 이후 데이터는 정확합니다. 두 시점이 걸친 기간을 보면 앞부분만
        적게 잡히니 주의하세요.
        <br />
        NTR 대화는 실제로는 DeepSeek 으로 생성되지만 차감은 캐릭터에 설정된 모델로
        기록됩니다. NTR 탭의 모델 이름은 실제 생성 모델이 아닙니다.
      </Footnote>
    </Wrapper>
  )
}

export default MessagePointData
